/* eslint-disable no-useless-escape */
import * as yup from 'yup';
import validUrl from 'valid-url';
import { StringSchema } from 'yup';
import { ObjectShape } from 'yup/lib/object';

declare module 'yup' {
  interface StringSchema {
    linksValidation(errorMessage: string): StringSchema;
  }
}

yup.addMethod<StringSchema>(yup.string, 'linksValidation', function (errorMessage) {
  return this.test('test-valid-link', errorMessage, (value) =>
    value ? !!validUrl.isWebUri(value as string) : true,
  );
});

export const loginValidation = yup
  .object({
    email: yup
      .string()
      .email('Enter the correct email!')
      .required('Please enter your email address!'),
    password: yup.string().required(),
  })
  .required();

export const inviteValidation = yup
  .object({
    firstName: yup.string().label('First name').trim().min(2).required('First name can’t be empty'),
    lastName: yup.string().label('Last name').trim().min(2).required('Last name can’t be empty'),
    email: yup
      .string()
      .email('Enter the correct email!')
      .required('Please enter your email address!'),
  })
  .required();

export const updateUserValidation = yup
  .object({
    firstName: yup.string().trim().required('First name can’t be empty'),
    lastName: yup.string().trim().required('Last name can’t be empty'),
    role: yup.string().required(),
    status: yup.string().required(),
  })
  .required();

export const resetPasswordValidation = yup
  .object({
    email: yup
      .string()
      .email('Enter the correct email!')
      .required('Please enter your email address!'),
  })
  .required();

export const accountValidation = yup
  .object({
    firstName: yup.string().trim().required('First name can’t be empty'),
    lastName: yup.string().trim().required('Last name can’t be empty'),
  })
  .required();

export const contentCreatorValidation = yup
  .object({
    firstName: yup.string().trim().required('First name can’t be empty'),
    companyName: yup.string().trim().required('Content creator name can’t be empty'),
    lastName: yup.string().trim(),
    transparencyReportLink: yup
      .string()
      .nullable(true)
      .linksValidation('Link must be valid')
      .transform((_, val) => val || null),
    subscriptionPlan: yup.string().required(),
    email: yup
      .string()
      .email('Enter the correct email!')
      .required('Please enter your email address!'),
    overrideFormulaCoef: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('Value must be a number'),
  })
  .required();

export const businessAccountValidation = yup
  .object({
    companyName: yup.string().label('Company name').trim().required(),
    email: yup
      .string()
      .label('Email')
      .email('Enter the correct email!')
      .required('Please enter your email address!'),
    firstName: yup.string().label('First name').trim().required(),
    lastName: yup.string().label('Last name').trim(),
    overrideFormulaCoef: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('Value must be a number'),
    slots: yup.object().shape({
      used: yup.number().required('Used is required'),
      available: yup
        .number()
        .transform((value, originalValue) => (`${originalValue}`.trim() === '' ? 0 : +value))
        .nullable()
        .required('Available slots is required')
        .typeError('Available slots must be a number')
        .min(1, 'Available slots must be greater than 0')
        .when('used', (used, schema) =>
          schema.test({
            name: 'is-greater-than-available',
            message: `Available slots must be more or equal to ${used} slots`,
            test: (available: string | number) => used <= +available,
          }),
        ),
    }),
  })
  .required();

export const getAssetAccountValidation = () =>
  yup
    .object({
      username: yup.string().label('Username').trim().required().min(2),
      transparencyReportLink: yup
        .string()
        .nullable(true)
        .linksValidation('Link must be valid')
        .transform((_, val) => val || null),
      properties: yup.object().nullable(true),
      firstName: yup
        .string()
        .label('First name')
        .trim()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .test(
          'min',
          'First name must be at least 2 characters',
          (value) => value === null || value === undefined || value.length >= 2,
        ),
      lastName: yup
        .string()
        .label('Last name')
        .trim()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .test(
          'min',
          'Last name must be at least 2 characters',
          (value) => value === null || value === undefined || value.length >= 2,
        ),
    })
    .required();

export const linksValidation = yup.object({
  links: yup.string(),
});

export const memberAccountValidation = yup
  .object({
    firstName: yup
      .string()
      .label('First name')
      .nullable()
      .trim()
      .min(2)
      .required('First name can’t be empty'),
    lastName: yup
      .string()
      .label('Last name')
      .nullable()
      .trim()
      .min(2)
      .required('Last name can’t be empty'),
    status: yup.string().nullable().trim().required('Status name can’t be empty'),
    role: yup.string().required(),
  })
  .required();

export const generalLinkInfoValidation = yup
  .object({
    overrideGoogleWeb: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val ? Number(val) : null)),
    overrideAtSource: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val ? Number(val) : null)),
  })
  .required();

export const createAccountValidation = yup
  .object({
    companyType: yup.string().required(),
    firstName: yup.string().label('First name').trim().min(2).required('First name can’t be empty'),
    lastName: yup.string().label('Last name').trim().min(2).required('Last name can’t be empty'),
    companyName: yup.string().label('Company name').trim().min(2),
    email: yup
      .string()
      .label('email')
      .email('Enter the correct email!')
      .required('Please enter your email address!'),
    slots: yup
      .number()
      .label('Slots')
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1')
      .min(1)
      .required('Slots can’t be empty'),
    plan: yup.string().required('Plan is required'),
  })
  .required();

export const createEnterpriseAccountValidation = yup
  .object({
    name: yup.string().required('Company name is required').trim(),
    subdomain: yup.string().required('Sub domain is required').trim(),
    plan: yup.string().trim().nullable(),
    logo: yup.string().trim().nullable(),
    logoFile: yup.mixed().nullable(),
  })
  .required();

export const createEnterpriseUsersValidation = yup
  .object({
    email: yup.string().email('Email must be a valid email').required('Email is required'),
    role: yup.mixed<'admin' | 'user'>().oneOf(['admin', 'user']).required('Role is required'),
  })
  .required();

export const generatePayLinkValidation = yup
  .object({
    plan: yup.string().required('Plan is required'),
    slots: yup
      .number()
      .label('Slots')
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1')
      .min(1)
      .required('Slots can’t be empty'),
    pricePerSlot: yup
      .number()
      .label('Price')
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1')
      .min(1)
      .required('Price can’t be empty'),
  })
  .required();

export const scanManagementDetailsValidation = yup
  .object({
    keywords: yup
      .array()
      .label('Keywords')
      .required()
      .of(yup.string().min(2).max(100))
      .min(1)
      .max(500)
      .transform((_, keys) => keys.map((key: string) => key.toLowerCase())),
    scanSources: yup.array().label('Scan type').required().of(yup.string()).min(1),
    pages: yup
      .number()
      .label('Pages')
      .required()
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1 to 100')
      .min(1, 'This value should be an integer from 1 to 100')
      .max(100, 'This value should be an integer from 1 to 100')
      .positive('This value should be an integer from 1 to 100')
      .integer('This value should be an integer from 1 to 100'),
    scanFrequencyDays: yup
      .number()
      .label('Quantity')
      .required()
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1 to 500')
      .min(1, 'This value should be an integer from 1 to 500')
      .max(500, 'This value should be an integer from 1 to 500')
      .positive('This value should be an integer from 1 to 500')
      .integer('This value should be an integer from 1 to 500'),
  })
  .required();

export const scanSettingsDetailsValidation = yup
  .object({
    keywords: yup
      .array()
      .label('Keywords')
      .of(yup.string().min(2).max(100))
      .max(500)
      .transform((_, keys) => keys.map((key: string) => key.toLowerCase())),
    usernames: yup
      .array()
      .label('Usernames')
      .of(yup.string().min(2).max(100))
      .max(500)
      .transform((_, keys) => keys.map((key: string) => key.replace(/\s/, '_').toLowerCase())),
    scanSources: yup.array().label('Scan type').of(yup.string()),
    filterNames: yup
      .array()
      .label('Filter Names')
      .of(yup.string().min(2).max(100))
      .max(500)
      .transform((_, keys) => keys.map((key: string) => key.toLowerCase())),
    filterRemovals: yup
      .array()
      .label('Filter Removals')
      .of(yup.string().min(2).max(100))
      .max(500)
      .transform((_, keys) => keys.map((key: string) => key.toLowerCase())),
    pages: yup
      .number()
      .label('Pages')
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1 to 100')
      .nullable(true)
      .min(1, 'This value should be an integer from 1 to 100')
      .max(100, 'This value should be an integer from 1 to 100')
      .positive('This value should be an integer from 1 to 100')
      .integer('This value should be an integer from 1 to 100'),
    scanFrequencyDays: yup
      .number()
      .label('Quantity')
      .transform((_, val) => (val ? Number(val) : null))
      .typeError('This value should be an integer from 1 to 500')
      .nullable(true)
      .min(1, 'This value should be an integer from 1 to 500')
      .max(500, 'This value should be an integer from 1 to 500')
      .positive('This value should be an integer from 1 to 500')
      .integer('This value should be an integer from 1 to 500'),
    aiFeatures: yup
      .object()
      .label('AI Features')
      .shape({
        faceMatch: yup.boolean(),
        nsfw: yup.boolean(),
        objectDetect: yup.boolean(),
        ocrDetect: yup.boolean(),
        people: yup.boolean(),
        watermark: yup.boolean(),
      })
      .optional(),
  })
  .required();

export const editLinkValidation = yup
  .object({
    link: yup.string().label('Domain').min(2).required(),
  })
  .required();

export const getSchemaAddAsset = (validationObject: ObjectShape) =>
  yup.object({
    username: yup.string().min(2),
    properties: yup.object(validationObject),
    firstName: yup
      .string()
      .label('First name')
      .trim()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .test(
        'min',
        'First name must be at least 2 characters',
        (value) => value === null || value === undefined || value.length >= 2,
      ),
    lastName: yup
      .string()
      .label('Last name')
      .trim()
      .nullable()
      .transform((value) => (value === '' ? null : value))
      .test(
        'min',
        'Last name must be at least 2 characters',
        (value) => value === null || value === undefined || value.length >= 2,
      ),
  });

export const getSchemaEditContract = yup
  .object({
    firstName: yup.string().label('First name').trim().required('First name can’t be empty'),
    lastName: yup.string().label('Last name').trim().required('Last name can’t be empty'),
    updateProfile: yup.boolean().optional(),
  })
  .required();

export const monitoringSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  photoUrl: yup.string().url('Must be a valid image URL').required('Photo URL is required'),
  isTalent: yup.boolean().nullable(),
  status: yup.number().required('Status is required'),
  category: yup.number().required('Category is required'),
  priority: yup
    .number()
    .transform((_, val) => (val ? Number(val) : null))
    .typeError('Priority is required')
    .min(0)
    .required('Priority is required'),
});

export const newsWireSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  url: yup.string().url('Must be a valid URL').required('URL is required'),
  imageUrl: yup.string().url('Must be a valid image URL').nullable(),
  label: yup.string().required('Label is required'),
});

export const sentimentAnalysisSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  url: yup.string().url('Must be a valid URL').required('URL is required'),
  imageUrl: yup.string().url('Must be a valid image URL').nullable(),
  category: yup.number().typeError('Category is required').required('Category is required'),
  subCategory: yup
    .number()
    .typeError('Subcategory is required')
    .required('Subcategory is required'),
  sentiment: yup.number().typeError('Sentiment is required').required('Sentiment is required'),
  label: yup.string().nullable(),
  isSubmitted: yup.boolean().default(false),
});
